/**
 * Trip Requests List Page Component
 * /admin/trip-requests
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TableSortLabel,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TableViewIcon from '@mui/icons-material/TableView';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import apiClient from '../api/client';

const EXPORT_FORMATS = [
  { key: 'csv', label: 'CSV', icon: <DescriptionIcon fontSize="small" /> },
  { key: 'xlsx', label: 'Excel', icon: <TableViewIcon fontSize="small" /> },
  { key: 'pdf', label: 'PDF', icon: <PictureAsPdfIcon fontSize="small" /> },
];

const TripRequests = () => {
  const [tripRequests, setTripRequests] = useState([]);
  const [allTripRequests, setAllTripRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerTypeFilter, setCustomerTypeFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [orderBy, setOrderBy] = useState('created_at');
  const [orderDirection, setOrderDirection] = useState('desc');
  const navigate = useNavigate();

  useEffect(() => {
    loadTripRequests();
  }, []);

  useEffect(() => {
    filterAndSortTripRequests();
  }, [statusFilter, customerTypeFilter, priorityFilter, startDateFilter, endDateFilter, searchTerm, orderBy, orderDirection, allTripRequests]);

  /**
   * The filter selection, as query parameters. Shared by the list and the
   * export so a download always matches what is on screen - if these ever
   * drift apart, the export silently misrepresents the list.
   */
  const buildFilterParams = () => {
    const params = {};
    if (searchTerm) params.search = searchTerm;
    if (statusFilter !== 'all') params.status = statusFilter;
    if (customerTypeFilter !== 'all') params.customer_type = customerTypeFilter;
    if (priorityFilter !== 'all') params.priority = priorityFilter;
    if (startDateFilter) params.start_date = startDateFilter;
    if (endDateFilter) params.end_date = endDateFilter;
    if (orderBy) {
      params.ordering = orderDirection === 'desc' ? `-${orderBy}` : orderBy;
    }
    return params;
  };

  const handleExport = async (exportFormat) => {
    setExportAnchorEl(null);
    try {
      setExporting(true);
      setError('');
      const { blob, filename } = await apiClient.downloadTripRequestsExport(
        buildFilterParams(),
        exportFormat
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const loadTripRequests = async () => {
    try {
      setLoading(true);
      setError('');
      const params = buildFilterParams();

      const response = await apiClient.getTripRequests(params);
      // Handle paginated response
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setAllTripRequests(data);
    } catch (err) {
      setError(err.message || 'Failed to load trip requests');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortTripRequests = () => {
    let filtered = [...allTripRequests];

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(tr => tr.status === statusFilter);
    }

    // Apply customer type filter
    if (customerTypeFilter !== 'all') {
      filtered = filtered.filter(tr => tr.customer_type === customerTypeFilter);
    }

    // Apply priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(tr => tr.priority === priorityFilter);
    }

    // Apply date range filter
    if (startDateFilter) {
      const startDate = new Date(startDateFilter);
      filtered = filtered.filter(tr => {
        if (!tr.scheduled_datetime) return false;
        const tripDate = new Date(tr.scheduled_datetime);
        return tripDate >= startDate;
      });
    }

    if (endDateFilter) {
      const endDate = new Date(endDateFilter);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(tr => {
        if (!tr.scheduled_datetime) return false;
        const tripDate = new Date(tr.scheduled_datetime);
        return tripDate <= endDate;
      });
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(tr => {
        const customerName = (tr.customer_detail?.customer_name || tr.customer_name || '').toLowerCase();
        const tripRef = (tr.trip_reference || tr.id || '').toLowerCase();
        const pickup = (tr.pickup_location || '').toLowerCase();
        const dropoff = (tr.dropoff_location || '').toLowerCase();
        return (
          customerName.includes(searchLower) ||
          tripRef.includes(searchLower) ||
          pickup.includes(searchLower) ||
          dropoff.includes(searchLower)
        );
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (orderBy) {
        case 'trip_reference':
          aValue = a.trip_reference || a.id || '';
          bValue = b.trip_reference || b.id || '';
          break;
        case 'customer_name':
          aValue = a.customer_detail?.customer_name || a.customer_name || '';
          bValue = b.customer_detail?.customer_name || b.customer_name || '';
          break;
        case 'pickup_location':
          aValue = a.pickup_location || '';
          bValue = b.pickup_location || '';
          break;
        case 'dropoff_location':
          aValue = a.dropoff_location || '';
          bValue = b.dropoff_location || '';
          break;
        case 'scheduled_datetime':
          aValue = a.scheduled_datetime ? new Date(a.scheduled_datetime).getTime() : 0;
          bValue = b.scheduled_datetime ? new Date(b.scheduled_datetime).getTime() : 0;
          break;
        case 'priority':
          const priorityOrder = { urgent: 3, high: 2, normal: 1, low: 0 };
          aValue = priorityOrder[a.priority] || 0;
          bValue = priorityOrder[b.priority] || 0;
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'created_at':
        default:
          aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
          bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
      }

      if (typeof aValue === 'string') {
        return orderDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return orderDirection === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      }
    });

    setTripRequests(filtered);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadTripRequests();
  };

  const handleClearFilters = () => {
    setStatusFilter('all');
    setCustomerTypeFilter('all');
    setPriorityFilter('all');
    setStartDateFilter('');
    setEndDateFilter('');
    setSearchTerm('');
    setOrderBy('created_at');
    setOrderDirection('desc');
    loadTripRequests();
  };

  const handleSort = (column) => {
    if (orderBy === column) {
      // Toggle direction
      setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setOrderBy(column);
      setOrderDirection('asc');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      assigned: 'info',
      confirmed: 'primary',
      active: 'success',
      completed: 'success',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      normal: 'default',
      high: 'warning',
      urgent: 'error',
    };
    return colors[priority] || 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const canEditTripRequest = (request) => {
    if (!request) return false;
    
    // Cannot edit if already converted to booking
    // Check for booking ID or booking_detail object (null/undefined means not converted)
    if (request.booking !== null && request.booking !== undefined) {
      return false;
    }
    if (request.booking_id !== null && request.booking_id !== undefined) {
      return false;
    }
    if (request.booking_detail !== null && request.booking_detail !== undefined) {
      return false;
    }
    
    // Cannot edit if status is completed or cancelled
    if (request.status === 'completed' || request.status === 'cancelled') {
      return false;
    }
    
    // Can edit if status is pending, assigned, or confirmed
    return ['pending', 'assigned', 'confirmed'].includes(request.status);
  };

  if (loading && tripRequests.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Trip Requests
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage incoming trip requests from customers
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={
              exporting ? <CircularProgress size={18} /> : <FileDownloadIcon />
            }
            disabled={exporting}
            onClick={(event) => setExportAnchorEl(event.currentTarget)}
          >
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
          <Menu
            anchorEl={exportAnchorEl}
            open={Boolean(exportAnchorEl)}
            onClose={() => setExportAnchorEl(null)}
          >
            {EXPORT_FORMATS.map((format) => (
              <MenuItem key={format.key} onClick={() => handleExport(format.key)}>
                <ListItemIcon>{format.icon}</ListItemIcon>
                <ListItemText>{format.label}</ListItemText>
              </MenuItem>
            ))}
          </Menu>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/trip-requests/new')}
          >
            New Trip Request
          </Button>
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        Export covers every row matching the current filters, not just this page.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <form onSubmit={handleSearchSubmit}>
              <TextField
                fullWidth
                placeholder="Search trip requests..."
                value={searchTerm}
                onChange={handleSearch}
                slotProps={{ input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                } }}
              />
            </form>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Customer Type</InputLabel>
              <Select
                value={customerTypeFilter}
                label="Customer Type"
                onChange={(e) => setCustomerTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="corporate">Corporate</MenuItem>
                <MenuItem value="direct">Direct</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={priorityFilter}
                label="Priority"
                onChange={(e) => setPriorityFilter(e.target.value)}
              >
                <MenuItem value="all">All Priorities</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={startDateFilter}
              onChange={(e) => setStartDateFilter(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={endDateFilter}
              onChange={(e) => setEndDateFilter(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {(statusFilter !== 'all' || customerTypeFilter !== 'all' || priorityFilter !== 'all' || startDateFilter || endDateFilter || searchTerm) && (
              <Button
                fullWidth
                variant="outlined"
                onClick={handleClearFilters}
                sx={{ height: '56px' }}
              >
                Clear Filters
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'trip_reference'}
                  direction={orderBy === 'trip_reference' ? orderDirection : 'asc'}
                  onClick={() => handleSort('trip_reference')}
                >
                  Reference
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'customer_name'}
                  direction={orderBy === 'customer_name' ? orderDirection : 'asc'}
                  onClick={() => handleSort('customer_name')}
                >
                  Customer
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'pickup_location'}
                  direction={orderBy === 'pickup_location' ? orderDirection : 'asc'}
                  onClick={() => handleSort('pickup_location')}
                >
                  Pickup
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'dropoff_location'}
                  direction={orderBy === 'dropoff_location' ? orderDirection : 'asc'}
                  onClick={() => handleSort('dropoff_location')}
                >
                  Dropoff
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'scheduled_datetime'}
                  direction={orderBy === 'scheduled_datetime' ? orderDirection : 'asc'}
                  onClick={() => handleSort('scheduled_datetime')}
                >
                  Scheduled
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'priority'}
                  direction={orderBy === 'priority' ? orderDirection : 'asc'}
                  onClick={() => handleSort('priority')}
                >
                  Priority
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? orderDirection : 'asc'}
                  onClick={() => handleSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>Journey</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tripRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No trip requests found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              tripRequests.map((request) => (
                <TableRow key={request.id} hover>
                  <TableCell>{request.trip_reference || request.id.substring(0, 8)}</TableCell>
                  <TableCell>
                    {request.customer_detail?.customer_name || request.customer_name || '-'}
                    <Chip
                      size="small"
                      sx={{ ml: 1 }}
                      variant="outlined"
                      label={request.request_profile === 'corporate' ? 'Corporate' : 'Direct'}
                    />
                  </TableCell>
                  {/* Origin and destination come from the first active leg once
                      one exists, falling back to the header fields that legacy
                      requests still carry. */}
                  <TableCell>
                    {(request.legs || []).find((l) => l.is_active !== false)?.pickup_location ||
                      request.pickup_location ||
                      '-'}
                  </TableCell>
                  <TableCell>
                    {(request.legs || []).find((l) => l.is_active !== false)?.dropoff_location ||
                      request.dropoff_location ||
                      '-'}
                  </TableCell>
                  <TableCell>
                    {formatDate(
                      (request.legs || []).find((l) => l.is_active !== false)
                        ?.requested_pickup_datetime || request.scheduled_datetime
                    )}
                    {(request.legs || []).filter((l) => l.is_active !== false).length > 1 && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        last:{' '}
                        {formatDate(
                          (request.legs || [])
                            .filter((l) => l.is_active !== false)
                            .slice(-1)[0]?.requested_pickup_datetime
                        )}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={request.priority || 'normal'}
                      color={getPriorityColor(request.priority)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={request.status || 'pending'}
                      color={getStatusColor(request.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={request.journey_type === 'round_trip' ? 'Round trip' : 'One way'}
                    />
                    <Typography variant="caption" display="block" color="text.secondary">
                      {request.declared_traveller_count || 1} traveller(s)
                      {(request.warnings || []).length > 0 ? ' · needs attention' : ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {canEditTripRequest(request) ? (
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/admin/trip-requests/${request.id}/edit`)}
                        title="Edit Trip Request"
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/admin/trip-requests/${request.id}`)}
                      title="View Details"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default TripRequests;

